import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useDroppable } from '@dnd-kit/core';
import { Folder } from '../../services/folders_api';
import { FolderGlyph, folderColor } from './folderVisuals';

interface FolderChipProps {
  folder: Folder;
  selected: boolean;
  onClick: () => void;
  // ドラッグ中はチップをドロップ先として強調する。
  dragInProgress?: boolean;
}

// フォルダバー上の1チップ。@dnd-kit のドロップ先を兼ねる（カードをここへ落とすと追加）。
const FolderChip: React.FC<FolderChipProps> = ({ folder, selected, onClick, dragInProgress }) => {
  const color = folderColor(folder.color);
  const [hovered, setHovered] = useState(false);
  const { setNodeRef, isOver } = useDroppable({
    id: `folder-${folder.id}`,
    data: { type: 'folder', folderId: folder.id },
  });

  return (
    <Box
      ref={setNodeRef}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.4, sm: 0.6 },
        flexShrink: 0,
        pl: { xs: 0.7, sm: 0.85 },
        pr: { xs: 0.85, sm: 1.05 },
        py: { xs: 0.3, sm: 0.45 },
        borderRadius: '8px',
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative',
        border: '1px solid',
        borderColor: isOver
          ? color
          : selected
          ? `${color}66`
          : dragInProgress
          ? `${color}55`
          : 'rgba(255,255,255,0.12)',
        backgroundColor: isOver
          ? `${color}38`
          : selected
          ? `${color}1f`
          : 'transparent',
        boxShadow: isOver ? `0 0 0 3px ${color}33` : 'none',
        transform: isOver ? 'scale(1.06)' : 'scale(1)',
        transition: 'background-color .15s ease, border-color .15s ease, transform .12s ease, box-shadow .15s ease',
        '&:hover': {
          backgroundColor: selected ? `${color}29` : 'rgba(255,255,255,0.05)',
        },
      }}
    >
      <FolderGlyph
        iconKey={folder.icon}
        open={selected || isOver}
        sx={{ fontSize: { xs: '0.82rem', sm: '0.95rem' }, color, flexShrink: 0 }}
      />
      <Typography
        sx={{
          fontSize: { xs: '0.66rem', sm: '0.74rem' },
          fontWeight: selected ? 500 : 400,
          color: selected ? 'text.primary' : 'text.secondary',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          maxWidth: { xs: 104, sm: 140 },
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {folder.name}
      </Typography>
      {!selected && (
        <Typography
          sx={{
            fontSize: { xs: '0.56rem', sm: '0.63rem' },
            fontWeight: 400,
            lineHeight: 1,
            ml: 0.1,
            color: isOver ? color : 'pulse.info',
          }}
        >
          {folder.item_count}
        </Typography>
      )}
      {/* 選択中: ホバーで件数→× に切り替え（解除ヒント） */}
      {selected && (
        <Box sx={{ ml: 0.1, width: '1.3ch', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {hovered ? (
            <CloseRoundedIcon sx={{ fontSize: { xs: '0.64rem', sm: '0.72rem' }, color: 'rgba(255,255,255,0.6)' }} />
          ) : (
            <Typography sx={{ fontSize: { xs: '0.56rem', sm: '0.63rem' }, fontWeight: 700, lineHeight: 1, color }}>
              {folder.item_count}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FolderChip;
